package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The glyph box for [LSectionTitle], measured in `em` rather than in [IconSize].
 *
 * **This is the one icon in the design system that must not take a rung of the scale.**
 * [LSectionTitle] inherits its font size from whatever card it stands in — that is the whole point
 * of the component, and the reason the heading resets `font-size` to `inherit` below. A section
 * title raised to [TypeScale.HeadingSmall] by its call site would keep a 16px icon beside 21px
 * text, and the pairing that looked deliberate at body size would look like a mistake one card
 * over. `1em` follows the text it was put next to, which is what a call site that changes the text
 * size is actually asking for.
 *
 * The size lands on the `svg` through the descendant rule for the same reason [LButtonIconStyle]
 * does it: a Lucide wrapper carries [LIconStyle]'s own 20px and ignores a width set on its parent.
 */
val LSectionTitleIconStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "inline-flex")
            property("flex", "0 0 auto")
            property("width", "1em")
            property("height", "1em")
        }
    }
    cssRule(" > svg") {
        Modifier.width(100.percent).height(100.percent)
    }
}

/**
 * The glyph box for [LSectionHeading], at [IconSize.MD].
 *
 * A fixed rung here and an `em` above, because these two components differ in exactly the way that
 * decides it: [LSectionHeading]'s title names its own size ([TypeScale.HeadingSmall]) and cannot be
 * resized by a call site, so a glyph beside it has one text size to answer to and can say which.
 */
val LSectionHeadingIconStyle = CssStyle {
    base {
        Modifier
            .width(IconSize.MD)
            .height(IconSize.MD)
            .styleModifier {
                property("display", "inline-flex")
                property("flex", "0 0 auto")
            }
    }
    cssRule(" > svg") {
        Modifier.width(100.percent).height(100.percent)
    }
}

/**
 * The title of a section inside a card — the line that names the content directly beneath it.
 *
 * **Twenty of these were `Span(Modifier.fontWeight(600))`.** Bold text is not a heading. `PageHeader`
 * emits the page's only `H1`, so a screen reader's heading list for a settings page contained one
 * entry and the page had no navigable structure at all: no way to jump to "Who holds one" on the
 * seats page, or to "Danger zone" on general. The reader's choice was to arrow through every row of
 * every table or to guess.
 *
 * `H2`, because the section sits directly under that `H1` and nothing intervenes. The same role had
 * been an `H2` on two pages, an `H3` on four, and a bold `Span` on twenty; a heading level that
 * depends on which page you are standing on is not structure either.
 *
 * **The tag changes and the pixels do not.** A `<h2>` arrives with the user agent's `1.5em` and its
 * block margins, which would have made this a visible redesign of every settings card rather than a
 * correction to their markup. Both are reset here, so the rendered line is byte-for-byte what the
 * `Span` drew: inherited body size, weight 600, no margin.
 *
 * Not for emphasis — a bold run inside a sentence is a `Span` and should stay one. This is only for
 * the line that a reader would expect to find in a table of contents.
 *
 * **A card's identity is not a section label.** Most of these name a section ("Delete this
 * workspace") and body size is right for them. A few name the *thing the card is about* — the plan
 * you are on, the workspace you are in — and those were `H3`s, so inheriting body size took them
 * down about 15% and flattened the card's hierarchy. Give those
 * `Modifier.fontSize(TypeScale.HeadingSmall)`: a step of the scale rather than the user agent's
 * `1.17em`, and the same decision made the same way on every call site that needs it.
 *
 * [icon] is `aria-hidden` and lives *inside* the heading, so the heading's accessible name stays
 * exactly [text] and a reader jumping between headings hears the same list they heard before. The
 * heading only becomes a flex row when there is a glyph to lay out, so a title without one renders
 * the markup it rendered before this parameter existed.
 */
@Composable
fun LSectionTitle(
    text: String,
    modifier: Modifier = Modifier,
    icon: (@Composable () -> Unit)? = null,
) {
    H2(
        Modifier
            .fontWeight(600)
            .margin(0.px)
            // `inherit`, not a token: these titles sit at body size inside a card, and naming a step
            // of the type scale here would silently resize twenty sections.
            .styleModifier { property("font-size", "inherit") }
            .then(
                if (icon != null) {
                    Modifier.gap(Space.SM).styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                    }
                } else {
                    Modifier
                },
            )
            .then(modifier)
            .toAttrs(),
    ) {
        if (icon != null) {
            Span(
                LSectionTitleIconStyle.toModifier()
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .toAttrs { attr("aria-hidden", "true") },
            ) {
                icon()
            }
        }
        Text(text)
    }
}

/**
 * A section's title and the sentence explaining it, as one block.
 *
 * Distinct from [LSectionTitle], which is the title alone at body size for a card that needs no
 * explanation. This is the heavier pairing a page uses to open a section it has to justify — the
 * pre-translate flow, an API token's scopes — where the description is doing real work and wants
 * the tighter spacing that keeps it attached to its heading.
 *
 * Extracted because two pages had written it privately and identically: same `HeadingSmall` at 700,
 * same `textSecondary` body-small paragraph, same zeroed margins, same `Space.XS` between them. Two
 * copies is the point at which the next page copies whichever it finds first.
 *
 * [actions] is the section's own controls, at the inline end and vertically centred against the
 * whole block rather than against the title alone — a "Add a locale" button beside a two-line
 * explanation reads as belonging to the section, and pinned to the heading it reads as belonging to
 * the sentence. Passing it wraps the block in a row; leaving it null renders the column exactly as
 * before, which is what the six existing call sites keep getting.
 */
@Composable
fun LSectionHeading(
    title: String,
    description: String,
    modifier: Modifier = Modifier,
    icon: (@Composable () -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null,
) {
    if (actions == null) {
        LSectionHeadingText(title, description, icon, modifier)
        return
    }
    Row(
        Modifier
            .width(100.percent)
            .gap(Space.LG)
            .flexWrap(FlexWrap.Wrap)
            .then(modifier),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LSectionHeadingText(
            title = title,
            description = description,
            icon = icon,
            modifier = Modifier.flexGrow(1).styleModifier { property("min-width", "0") },
        )
        Row(
            Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            actions()
        }
    }
}

@Composable
private fun LSectionHeadingText(
    title: String,
    description: String,
    icon: (@Composable () -> Unit)?,
    modifier: Modifier,
) {
    Column(Modifier.gap(Space.XS).then(modifier)) {
        if (icon != null) {
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                Span(
                    LSectionHeadingIconStyle.toModifier()
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        .toAttrs { attr("aria-hidden", "true") },
                ) {
                    icon()
                }
                LSectionHeadingTitle(title)
            }
        } else {
            LSectionHeadingTitle(title)
        }
        P(
            Modifier
                .color(ColorMode.current.toSitePalette().textSecondary)
                .fontSize(TypeScale.BodySmall)
                .lineHeight(TypeScale.BodyLineHeight)
                .margin(0.px)
                .toAttrs(),
        ) { Text(description) }
    }
}

@Composable
private fun LSectionHeadingTitle(title: String) {
    H2(
        Modifier
            .fontSize(TypeScale.HeadingSmall)
            .fontWeight(700)
            .margin(0.px)
            .toAttrs(),
    ) { Text(title) }
}
